// src/contexts/AuthContext.jsx
import React, { createContext, useState, useContext, useEffect } from 'react';
import { supabase } from '../supabaseClient';
import { useUserStore } from '../store/userStore';

const AuthContext = createContext();

export const AuthProvider = ({ children }) => {
    const [user, setUser] = useState(null);
    const [loading, setLoading] = useState(true);
    const setGoogleData = useUserStore((state) => state.setGoogleData);
    const clearUser = useUserStore((state) => state.clearUser);

    useEffect(() => {
        // IMPORTANT: this callback must stay synchronous and must NOT
        // `await` any other supabase.from()/storage/functions call directly
        // inside it — doing so deadlocks the Supabase auth client and hangs
        // every subsequent Supabase call in the app (this is what broke
        // sign-in and logout last time). Set user state immediately here,
        // and defer any extra async lookups with setTimeout so they run
        // after this callback has returned.
        const { data: { subscription } } = supabase.auth.onAuthStateChange((event, session) => {
            if (session?.user) {
                setUser(session.user);
                setGoogleData(session.user);
                setLoading(false);

                // Deferred, non-blocking check for a pending_deletion account.
                // Runs after the callback finishes, so it can't deadlock anything.
                setTimeout(async () => {
                    const { data: profile, error: profileError } = await supabase
                        .from('profiles')
                        .select('account_status')
                        .eq('id', session.user.id)
                        .single();

                    if (!profileError && profile?.account_status === 'pending_deletion') {
                        await supabase.auth.signOut();
                        setUser(null);
                        clearUser();
                    }
                }, 0);
            } else {
                setUser(null);
                clearUser();
                setLoading(false);
            }
        });

        return () => subscription.unsubscribe();
    }, []);

    const signIn = async (email, password) => {
        return await supabase.auth.signInWithPassword({ email, password });
    };

    // New: Social Google OAuth Provider workflow handler
    const signInWithGoogle = async () => {
        return await supabase.auth.signInWithOAuth({
            provider: 'google',
            options: {
                // Dynamically detects if you're on localhost:3000 or production domain
                redirectTo: window.location.origin,
            },
        });
    };

    const signOut = async () => {
        await supabase.auth.signOut();
        clearUser();
    };

    return (
        <AuthContext.Provider value={{
            user,
            loading,
            signIn,
            signInWithGoogle,
            signOut
        }}>
            {children}
        </AuthContext.Provider>
    );
};

export const useAuth = () => useContext(AuthContext);